<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import inboxesAPI from 'dashboard/api/inboxes';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inboxId: {
    type: [String, Number],
    required: true,
  },
});

const router = useRouter();
const store = useStore();

const TEMPLATE_STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'APPROVED', label: 'Ativo - aprovado' },
  { value: 'PENDING', label: 'Em analise' },
  { value: 'REJECTED', label: 'Reprovado' },
  { value: 'PAUSED', label: 'Pausado' },
  { value: 'DISABLED', label: 'Desativado' },
];

const TEMPLATE_CATEGORY_OPTIONS = [
  { value: '', label: 'Todas as categorias' },
  { value: 'UTILITY', label: 'Utilidade' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'AUTHENTICATION', label: 'Autenticacao' },
];

const TEMPLATE_LANGUAGE_OPTIONS = [
  { value: 'pt_BR', label: 'Portugues (BR)' },
  { value: 'en_US', label: 'English (US)' },
  { value: 'es', label: 'Espanhol' },
];

const HEADER_FORMAT_OPTIONS = [
  { value: 'NONE', label: 'Nenhum' },
  { value: 'TEXT', label: 'Texto' },
  { value: 'IMAGE', label: 'Imagem' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'DOCUMENT', label: 'PDF / documento' },
  { value: 'LOCATION', label: 'Localizacao' },
];

const BUTTON_TYPE_OPTIONS = [
  { value: 'QUICK_REPLY', label: 'Resposta rapida' },
  { value: 'URL', label: 'Abrir site' },
  { value: 'PHONE_NUMBER', label: 'Ligar' },
  { value: 'COPY_CODE', label: 'Copiar codigo' },
  { value: 'FLOW', label: 'Fluxo' },
  { value: 'CATALOG', label: 'Catalogo' },
];

const COMMON_EMOJIS = ['🙂', '👍', '🙏', '✅', '📄', '🏠', '💬', '📞'];

const templates = ref([]);
const selectedTemplate = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);
const errorMessage = ref('');
const mode = ref('view');
const filters = ref({
  query: '',
  status: '',
  category: '',
});

const cachedTemplates = computed(
  () => store.getters['inboxes/getWhatsAppTemplates'](props.inboxId) || []
);

const emptyForm = () => ({
  name: '',
  category: 'UTILITY',
  language: 'pt_BR',
  parameter_format: 'POSITIONAL',
  allow_category_change: true,
  header_format: 'NONE',
  header_text: '',
  header_example: '',
  header_handle: '',
  body: 'Ola, {{1}}! Como podemos ajudar?',
  body_examples: ['Maria'],
  footer_text: '',
  buttons: [],
  ttl_enabled: false,
  message_send_ttl_seconds: 600,
});

const form = ref(emptyForm());

const normalizeTemplateName = name =>
  String(name || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');

const statusLabel = status =>
  TEMPLATE_STATUS_OPTIONS.find(item => item.value === status)?.label ||
  status ||
  'Sem status';

const categoryLabel = category =>
  TEMPLATE_CATEGORY_OPTIONS.find(item => item.value === category)?.label ||
  category ||
  'Sem categoria';

const bodyText = template =>
  template?.components?.find(component => component.type === 'BODY')?.text || '';

const footerText = template =>
  template?.components?.find(component => component.type === 'FOOTER')?.text || '';

const buttons = template =>
  template?.components?.find(component => component.type === 'BUTTONS')?.buttons || [];

const headerComponent = template =>
  template?.components?.find(component => component.type === 'HEADER') || null;

const variableIndexes = text =>
  [...String(text || '').matchAll(/{{\s*(\d+)\s*}}/g)]
    .map(match => Number(match[1]))
    .filter(Boolean)
    .sort((a, b) => a - b);

const uniqueVariableIndexes = text => [...new Set(variableIndexes(text))];

const bodyVariables = computed(() => uniqueVariableIndexes(form.value.body));
const headerVariables = computed(() => uniqueVariableIndexes(form.value.header_text));

const ensureBodyExamples = () => {
  const next = [...form.value.body_examples];
  while (next.length < bodyVariables.value.length) {
    next.push('');
  }
  form.value.body_examples = next.slice(0, bodyVariables.value.length);
};

const previewText = computed(() => {
  let text = form.value.body || '';
  bodyVariables.value.forEach((index, position) => {
    const example = form.value.body_examples[position] || `valor ${index}`;
    text = text.replace(new RegExp(`{{\\s*${index}\\s*}}`, 'g'), example);
  });
  return text;
});

const headerPreview = computed(() => {
  if (form.value.header_format === 'TEXT') {
    let text = form.value.header_text || '';
    headerVariables.value.forEach(index => {
      text = text.replace(
        new RegExp(`{{\\s*${index}\\s*}}`, 'g'),
        form.value.header_example || `valor ${index}`
      );
    });
    return text;
  }
  return form.value.header_format === 'NONE' ? '' : form.value.header_format;
});

const extractBodyExamples = component => {
  const bodyTextExamples = component?.example?.body_text;
  if (Array.isArray(bodyTextExamples?.[0])) return bodyTextExamples[0];
  if (Array.isArray(bodyTextExamples)) return bodyTextExamples;
  return [];
};

const extractHeaderExample = component => {
  if (component?.format === 'TEXT') {
    return component?.example?.header_text?.[0] || '';
  }
  return component?.example?.header_handle?.[0] || '';
};

const normalizeButtonsForForm = nextButtons =>
  nextButtons.map(button => ({
    type: button.type || 'QUICK_REPLY',
    text:
      button.text ||
      (button.type === 'COPY_CODE' ? 'Copiar codigo' : ''),
    url: button.url || '',
    phone_number: button.phone_number || '',
    example: Array.isArray(button.example)
      ? button.example[0] || ''
      : button.example || '',
    flow_id: button.flow_id || '',
    flow_action: button.flow_action || 'NAVIGATE',
    navigate_screen: button.navigate_screen || '',
  }));

const filteredTemplates = computed(() => {
  const query = filters.value.query.trim().toLowerCase();
  return templates.value.filter(template => {
    const matchesQuery =
      !query ||
      template.name?.toLowerCase().includes(query) ||
      bodyText(template).toLowerCase().includes(query);
    const matchesStatus =
      !filters.value.status || template.status === filters.value.status;
    const matchesCategory =
      !filters.value.category || template.category === filters.value.category;

    return matchesQuery && matchesStatus && matchesCategory;
  });
});

const approvedCount = computed(
  () => templates.value.filter(template => template.status === 'APPROVED').length
);

const responseTemplates = payload => {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.templates)) return payload.templates;
  if (Array.isArray(payload?.message_templates)) return payload.message_templates;
  if (Array.isArray(payload?.payload?.data)) return payload.payload.data;
  return [];
};

const setTemplates = nextTemplates => {
  templates.value = Array.isArray(nextTemplates) ? nextTemplates : [];
  if (!selectedTemplate.value && templates.value.length) {
    selectTemplate(templates.value[0]);
  }
};

const loadTemplates = async ({ force = false } = {}) => {
  isLoading.value = true;
  errorMessage.value = '';

  try {
    const { data } = await inboxesAPI.getWhatsappTemplates(props.inboxId, {
      limit: 500,
      force,
      _: Date.now(),
    });
    const loadedTemplates = responseTemplates(data);
    setTemplates(
      loadedTemplates.length || force ? loadedTemplates : cachedTemplates.value
    );
  } catch (error) {
    if (!force && cachedTemplates.value.length) {
      setTemplates(cachedTemplates.value);
      return;
    }

    errorMessage.value =
      error?.response?.data?.error?.message ||
      error?.response?.data?.message ||
      'Nao foi possivel carregar os templates do WhatsApp';
  } finally {
    isLoading.value = false;
  }
};

const selectTemplate = template => {
  const header = headerComponent(template);
  const body = template?.components?.find(component => component.type === 'BODY');
  selectedTemplate.value = template;
  mode.value = 'view';
  form.value = {
    name: template.name || '',
    category: template.category || 'UTILITY',
    language: template.language || 'pt_BR',
    parameter_format: template.parameter_format || 'POSITIONAL',
    allow_category_change: true,
    header_format: header?.format || 'NONE',
    header_text: header?.format === 'TEXT' ? header.text || '' : '',
    header_example: header?.format === 'TEXT' ? extractHeaderExample(header) : '',
    header_handle: header?.format && header.format !== 'TEXT' ? extractHeaderExample(header) : '',
    body: bodyText(template),
    body_examples: extractBodyExamples(body),
    footer_text: footerText(template),
    buttons: normalizeButtonsForForm(buttons(template)),
    ttl_enabled: false,
    message_send_ttl_seconds: 600,
  };
  ensureBodyExamples();
  errorMessage.value = '';
};

const startCreate = () => {
  selectedTemplate.value = null;
  mode.value = 'create';
  form.value = emptyForm();
  ensureBodyExamples();
  errorMessage.value = '';
};

const startEdit = () => {
  if (!selectedTemplate.value) return;
  mode.value = 'edit';
  ensureBodyExamples();
  errorMessage.value = '';
};

const insertIntoText = (field, text) => {
  form.value[field] = `${form.value[field] || ''}${text}`;
  ensureBodyExamples();
};

const wrapBodySelection = (prefix, suffix = prefix) => {
  form.value.body = `${prefix}${form.value.body || ''}${suffix}`;
};

const addBodyVariable = () => {
  const next = bodyVariables.value.length + 1;
  insertIntoText('body', `{{${next}}}`);
};

const addHeaderVariable = () => {
  form.value.header_text = `${form.value.header_text || ''}{{1}}`;
};

const addButton = type => {
  if (!type) return;
  if (form.value.buttons.length >= 10) return;
  form.value.buttons.push({
    type,
    text: type === 'COPY_CODE' ? 'Copiar codigo' : '',
    url: '',
    phone_number: '',
    example: '',
    flow_id: '',
    flow_action: 'NAVIGATE',
    navigate_screen: '',
  });
};

const removeButton = index => {
  form.value.buttons.splice(index, 1);
};

const onAddButtonType = event => {
  addButton(event.target.value);
  event.target.value = '';
};

const buttonPayload = button => {
  const base = { type: button.type };
  if (!['COPY_CODE', 'CATALOG'].includes(button.type)) {
    base.text = button.text.trim();
  }
  if (button.type === 'URL') {
    return {
      ...base,
      url: button.url.trim(),
      ...(button.url.includes('{{1}}') && button.example.trim()
        ? { example: [button.example.trim()] }
        : {}),
    };
  }
  if (button.type === 'PHONE_NUMBER') {
    return { ...base, phone_number: button.phone_number.trim() };
  }
  if (button.type === 'COPY_CODE') {
    return { ...base, example: button.example.trim() || 'CODIGO' };
  }
  if (button.type === 'FLOW') {
    return {
      ...base,
      flow_id: button.flow_id.trim(),
      flow_action: button.flow_action || 'NAVIGATE',
      ...(button.navigate_screen.trim()
        ? { navigate_screen: button.navigate_screen.trim() }
        : {}),
    };
  }
  return base;
};

const templatePayload = () => {
  ensureBodyExamples();

  const components = [];

  if (form.value.header_format === 'TEXT' && form.value.header_text.trim()) {
    components.push({
      type: 'HEADER',
      format: 'TEXT',
      text: form.value.header_text.trim(),
      ...(headerVariables.value.length && form.value.header_example.trim()
        ? { example: { header_text: [form.value.header_example.trim()] } }
        : {}),
    });
  } else if (
    form.value.header_format !== 'NONE' &&
    form.value.header_handle.trim()
  ) {
    components.push({
      type: 'HEADER',
      format: form.value.header_format,
      example: { header_handle: [form.value.header_handle.trim()] },
    });
  }

  components.push({
    type: 'BODY',
    text: form.value.body.trim(),
    ...(bodyVariables.value.length
      ? { example: { body_text: [form.value.body_examples] } }
      : {}),
  });

  if (form.value.footer_text.trim()) {
    components.push({ type: 'FOOTER', text: form.value.footer_text.trim() });
  }

  const nextButtons = form.value.buttons
    .filter(button => {
      if (button.type === 'COPY_CODE') return button.example.trim();
      if (button.type === 'CATALOG') return true;
      return button.text.trim();
    })
    .map(buttonPayload);
  if (nextButtons.length) {
    components.push({ type: 'BUTTONS', buttons: nextButtons });
  }

  return {
    name: normalizeTemplateName(form.value.name),
    category: form.value.category,
    language: form.value.language.trim(),
    parameter_format: form.value.parameter_format,
    allow_category_change: form.value.allow_category_change,
    ...(form.value.ttl_enabled
      ? {
          message_send_ttl_seconds: Number(
            form.value.message_send_ttl_seconds || 600
          ),
        }
      : {}),
    components,
  };
};

const saveTemplate = async () => {
  const payload = templatePayload();
  const bodyComponent = payload.components.find(component => component.type === 'BODY');

  if (!payload.name || !bodyComponent?.text) {
    errorMessage.value = 'Informe nome e corpo do template';
    return;
  }

  isSaving.value = true;
  errorMessage.value = '';

  try {
    if (mode.value === 'edit' && selectedTemplate.value?.id) {
      await inboxesAPI.updateWhatsappTemplate(
        props.inboxId,
        selectedTemplate.value.id,
        payload
      );
      useAlert('Template enviado para atualizacao na Meta');
    } else {
      await inboxesAPI.createWhatsappTemplate(props.inboxId, payload);
    useAlert('Template criado e enviado para analise da Meta');
    }
    selectedTemplate.value = null;
    mode.value = 'view';
    await loadTemplates({ force: true });
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.error?.message ||
      error?.response?.data?.message ||
      'Nao foi possivel salvar o template na Meta';
  } finally {
    isSaving.value = false;
  }
};

const deleteTemplate = async () => {
  if (!selectedTemplate.value?.name) return;
  // eslint-disable-next-line no-alert
  if (!window.confirm(`Excluir o template "${selectedTemplate.value.name}"?`)) {
    return;
  }

  isSaving.value = true;
  errorMessage.value = '';

  try {
    await inboxesAPI.deleteWhatsappTemplate(
      props.inboxId,
      selectedTemplate.value.name
    );
    useAlert('Template excluido na Meta');
    selectedTemplate.value = null;
    await loadTemplates({ force: true });
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.error?.message ||
      error?.response?.data?.message ||
      'Nao foi possivel excluir o template na Meta';
  } finally {
    isSaving.value = false;
  }
};

onMounted(loadTemplates);
</script>

<template>
  <main class="flex-1 min-w-0 h-full overflow-auto bg-n-background">
    <section class="px-6 py-6">
      <header class="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <button
            type="button"
            class="mb-3 inline-flex items-center gap-2 text-sm text-n-slate-11 hover:text-n-slate-12"
            @click="router.back()"
          >
            <span class="i-lucide-arrow-left size-4" />
            Voltar
          </button>
          <h1 class="text-2xl font-semibold text-n-slate-12">
            Modelos de mensagem
          </h1>
          <p class="mt-1 text-sm text-n-slate-11">
            Gerencie os templates da Meta desta conexao.
          </p>
        </div>
        <div class="flex items-center gap-2">
          <Button
            label="Criar modelo"
            icon="i-lucide-plus"
            size="sm"
            @click="startCreate"
          />
          <Button
            label="Atualizar lista"
            icon="i-lucide-refresh-cw"
            size="sm"
            slate
            :is-loading="isLoading"
            @click="loadTemplates({ force: true })"
          />
        </div>
      </header>

      <div
        v-if="errorMessage"
        class="mb-4 rounded-md border border-solid border-n-ruby-5 bg-n-ruby-2 px-3 py-2 text-sm text-n-ruby-11"
      >
        {{ errorMessage }}
      </div>

      <div class="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_420px]">
        <section class="overflow-hidden border rounded-lg border-n-weak">
          <div
            class="grid grid-cols-1 gap-2 p-4 border-b border-n-weak md:grid-cols-[1fr_180px_200px]"
          >
            <label
              class="flex items-center gap-2 px-3 border rounded-md border-n-weak bg-n-alpha-2"
            >
              <span class="i-lucide-search size-4 text-n-slate-10" />
              <input
                v-model="filters.query"
                placeholder="Pesquisar"
                class="w-full h-10 text-sm bg-transparent outline-none text-n-slate-12"
              />
            </label>
            <select
              v-model="filters.category"
              class="h-10 px-3 text-sm border rounded-md border-n-weak bg-n-alpha-2 text-n-slate-12"
            >
              <option
                v-for="option in TEMPLATE_CATEGORY_OPTIONS"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
            <select
              v-model="filters.status"
              class="h-10 px-3 text-sm border rounded-md border-n-weak bg-n-alpha-2 text-n-slate-12"
            >
              <option
                v-for="option in TEMPLATE_STATUS_OPTIONS"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>

          <div class="flex items-center gap-3 px-4 py-3 text-xs text-n-slate-10">
            <span>{{ templates.length }} modelos</span>
            <span>{{ approvedCount }} aprovados</span>
          </div>

          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead class="text-left border-y border-n-weak bg-n-alpha-2">
                <tr class="text-n-slate-10">
                  <th class="px-4 py-3 font-medium">Nome do modelo</th>
                  <th class="px-4 py-3 font-medium">Categoria</th>
                  <th class="px-4 py-3 font-medium">Idioma</th>
                  <th class="px-4 py-3 font-medium">Status</th>
                  <th class="px-4 py-3 font-medium">Mensagem</th>
                  <th class="px-4 py-3 font-medium">Acoes</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="template in filteredTemplates"
                  :key="`${template.id}-${template.name}-${template.language}`"
                  class="border-b cursor-pointer border-n-weak hover:bg-n-alpha-2"
                  :class="{
                    'bg-n-blue-2': selectedTemplate?.id === template.id,
                  }"
                  @click="selectTemplate(template)"
                >
                  <td class="px-4 py-3">
                    <p class="m-0 font-medium text-n-slate-12">
                      {{ template.name }}
                    </p>
                    <p class="m-0 mt-1 text-xs text-n-slate-10">
                      ID {{ template.id || '-' }}
                    </p>
                  </td>
                  <td class="px-4 py-3 text-n-slate-11">
                    {{ categoryLabel(template.category) }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-11">
                    {{ template.language }}
                  </td>
                  <td class="px-4 py-3">
                    <span
                      class="rounded-md px-2 py-1 text-xs font-medium"
                      :class="
                        template.status === 'APPROVED'
                          ? 'bg-n-teal-3 text-n-teal-11'
                          : 'bg-n-amber-3 text-n-amber-11'
                      "
                    >
                      {{ statusLabel(template.status) }}
                    </span>
                  </td>
                  <td class="max-w-md px-4 py-3 text-n-slate-11">
                    <p class="m-0 line-clamp-2">
                      {{ bodyText(template) || '-' }}
                    </p>
                  </td>
                  <td class="px-4 py-3">
                    <Button
                      icon="i-lucide-pencil"
                      xs
                      slate
                      faded
                      @click.stop="selectTemplate(template); startEdit()"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div
            v-if="isLoading"
            class="flex items-center justify-center h-40 text-sm text-n-slate-11"
          >
            <span class="i-lucide-loader-2 mr-2 size-5 animate-spin" />
            Carregando templates
          </div>
          <div
            v-else-if="!filteredTemplates.length"
            class="flex items-center justify-center h-40 text-sm text-n-slate-10"
          >
            Nenhum template encontrado.
          </div>
        </section>

        <aside class="border rounded-lg border-n-weak bg-n-alpha-2">
          <div class="p-4 border-b border-n-weak">
            <h2 class="text-base font-semibold text-n-slate-12">
              {{
                mode === 'create'
                  ? 'Criar modelo'
                  : mode === 'edit'
                    ? 'Editar modelo'
                    : 'Detalhes do modelo'
              }}
            </h2>
            <p class="mt-1 text-xs text-n-slate-10">
              Alteracoes podem voltar para analise da Meta.
            </p>
          </div>

          <form
            v-if="mode === 'create' || mode === 'edit'"
            class="p-4 space-y-3"
            @submit.prevent="saveTemplate"
          >
            <section class="rounded-lg border border-n-weak bg-n-background p-3">
              <div class="grid grid-cols-2 gap-2">
                <label class="block col-span-2">
                  <span class="block mb-1 text-xs font-medium text-n-slate-10">
                    Nome do modelo
                  </span>
                  <input
                    v-model="form.name"
                    :disabled="mode === 'edit'"
                    placeholder="nome_do_template"
                    maxlength="512"
                    class="w-full h-10 px-3 text-sm border rounded-md border-n-weak bg-n-alpha-2 text-n-slate-12 disabled:opacity-60"
                  />
                </label>

                <label class="block">
                  <span class="block mb-1 text-xs font-medium text-n-slate-10">
                    Categoria
                  </span>
                  <select
                    v-model="form.category"
                    class="w-full h-10 px-3 text-sm border rounded-md border-n-weak bg-n-alpha-2 text-n-slate-12"
                  >
                    <option value="UTILITY">Utilidade</option>
                    <option value="MARKETING">Marketing</option>
                    <option value="AUTHENTICATION">Autenticacao</option>
                  </select>
                </label>
                <label class="block">
                  <span class="block mb-1 text-xs font-medium text-n-slate-10">
                    Idioma
                  </span>
                  <select
                    v-model="form.language"
                    :disabled="mode === 'edit'"
                    class="w-full h-10 px-3 text-sm border rounded-md border-n-weak bg-n-alpha-2 text-n-slate-12 disabled:opacity-60"
                  >
                    <option
                      v-for="option in TEMPLATE_LANGUAGE_OPTIONS"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </label>
              </div>
              <label class="mt-3 flex items-center gap-2 text-xs text-n-slate-11">
                <input v-model="form.allow_category_change" type="checkbox" />
                Permitir que a Meta ajuste a categoria se necessario
              </label>
            </section>

            <section class="rounded-lg border border-n-weak bg-n-background p-3">
              <h3 class="m-0 mb-3 text-sm font-semibold text-n-slate-12">
                Cabecalho
              </h3>
              <label class="block">
                <span class="block mb-1 text-xs font-medium text-n-slate-10">
                  Tipo
                </span>
                <select
                  v-model="form.header_format"
                  class="w-full h-10 px-3 text-sm border rounded-md border-n-weak bg-n-alpha-2 text-n-slate-12"
                >
                  <option
                    v-for="option in HEADER_FORMAT_OPTIONS"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </label>

              <template v-if="form.header_format === 'TEXT'">
                <label class="block mt-2">
                  <span class="block mb-1 text-xs font-medium text-n-slate-10">
                    Texto do cabecalho
                  </span>
                  <input
                    v-model="form.header_text"
                    maxlength="60"
                    class="w-full h-10 px-3 text-sm border rounded-md border-n-weak bg-n-alpha-2 text-n-slate-12"
                  />
                </label>
                <div class="mt-2 flex justify-end">
                  <button
                    type="button"
                    class="inline-flex items-center gap-1 text-xs text-n-blue-11"
                    @click="addHeaderVariable"
                  >
                    <span class="i-lucide-plus size-3" />
                    Adicionar variavel
                  </button>
                </div>
                <label v-if="headerVariables.length" class="block mt-2">
                  <span class="block mb-1 text-xs font-medium text-n-slate-10">
                    Exemplo da variavel do cabecalho
                  </span>
                  <input
                    v-model="form.header_example"
                    placeholder="Ex: Maria"
                    class="w-full h-10 px-3 text-sm border rounded-md border-n-weak bg-n-alpha-2 text-n-slate-12"
                  />
                </label>
              </template>
              <label
                v-else-if="form.header_format !== 'NONE'"
                class="block mt-2"
              >
                <span class="block mb-1 text-xs font-medium text-n-slate-10">
                  Handle da amostra de midia da Meta
                </span>
                <input
                  v-model="form.header_handle"
                  placeholder="Cole o header_handle da amostra"
                  class="w-full h-10 px-3 text-sm border rounded-md border-n-weak bg-n-alpha-2 text-n-slate-12"
                />
              </label>
            </section>

            <section class="rounded-lg border border-n-weak bg-n-background p-3">
              <div class="mb-2 flex items-center justify-between gap-2">
                <h3 class="m-0 text-sm font-semibold text-n-slate-12">
                  Corpo
                </h3>
                <div class="flex items-center gap-1">
                  <button
                    v-for="emoji in COMMON_EMOJIS"
                    :key="emoji"
                    type="button"
                    class="h-7 w-7 rounded-md hover:bg-n-alpha-2"
                    @click="insertIntoText('body', emoji)"
                  >
                    {{ emoji }}
                  </button>
                </div>
              </div>
              <textarea
                v-model="form.body"
                rows="8"
                maxlength="1024"
                class="w-full px-3 py-2 text-sm border rounded-md resize-y border-n-weak bg-n-alpha-2 text-n-slate-12"
                @input="ensureBodyExamples"
              />
              <div class="mt-2 flex flex-wrap items-center justify-between gap-2">
                <div class="flex items-center gap-1">
                  <button type="button" class="px-2 py-1 text-sm font-bold" @click="wrapBodySelection('*')">B</button>
                  <button type="button" class="px-2 py-1 text-sm italic" @click="wrapBodySelection('_')">I</button>
                  <button type="button" class="px-2 py-1 text-sm line-through" @click="wrapBodySelection('~')">S</button>
                  <button type="button" class="px-2 py-1 text-sm font-mono" @click="wrapBodySelection('```', '```')">&lt;/&gt;</button>
                </div>
                <button
                  type="button"
                  class="inline-flex items-center gap-1 text-xs text-n-blue-11"
                  @click="addBodyVariable"
                >
                  <span class="i-lucide-plus size-3" />
                  Adicionar variavel
                </button>
              </div>

              <div v-if="bodyVariables.length" class="mt-3 space-y-2">
                <p class="m-0 text-xs font-medium text-n-slate-10">
                  Exemplos das variaveis
                </p>
                <label
                  v-for="(variable, index) in bodyVariables"
                  :key="variable"
                  class="block"
                >
                  <span class="mb-1 block text-xs text-n-slate-10">
                    Variavel {{ variable }}
                  </span>
                  <input
                    v-model="form.body_examples[index]"
                    :placeholder="`Exemplo para {{${variable}}}`"
                    class="w-full h-9 px-3 text-sm border rounded-md border-n-weak bg-n-alpha-2 text-n-slate-12"
                  />
                </label>
              </div>
            </section>

            <section class="rounded-lg border border-n-weak bg-n-background p-3">
              <h3 class="m-0 mb-2 text-sm font-semibold text-n-slate-12">
                Rodape
              </h3>
              <input
                v-model="form.footer_text"
                maxlength="60"
                placeholder="Texto opcional no final da mensagem"
                class="w-full h-10 px-3 text-sm border rounded-md border-n-weak bg-n-alpha-2 text-n-slate-12"
              />
            </section>

            <section class="rounded-lg border border-n-weak bg-n-background p-3">
              <div class="mb-2 flex items-center justify-between gap-2">
                <h3 class="m-0 text-sm font-semibold text-n-slate-12">
                  Botoes
                </h3>
                <select
                  class="h-9 px-2 text-xs border rounded-md border-n-weak bg-n-alpha-2 text-n-slate-12"
                  @change="onAddButtonType"
                >
                  <option value="">Adicionar botao</option>
                  <option
                    v-for="option in BUTTON_TYPE_OPTIONS"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </div>
              <div v-if="!form.buttons.length" class="text-xs text-n-slate-10">
                Adicione respostas rapidas, site, telefone ou copiar codigo.
              </div>
              <div
                v-for="(buttonItem, index) in form.buttons"
                :key="index"
                class="mb-2 rounded-md border border-n-weak bg-n-alpha-2 p-2"
              >
                <div class="mb-2 flex items-center justify-between gap-2">
                  <select
                    v-model="buttonItem.type"
                    class="h-9 px-2 text-xs border rounded-md border-n-weak bg-n-background text-n-slate-12"
                  >
                    <option
                      v-for="option in BUTTON_TYPE_OPTIONS"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                  <button
                    type="button"
                    class="i-lucide-trash-2 size-4 text-n-ruby-10"
                    @click="removeButton(index)"
                  />
                </div>
                <input
                  v-if="buttonItem.type !== 'COPY_CODE' && buttonItem.type !== 'CATALOG'"
                  v-model="buttonItem.text"
                  maxlength="25"
                  placeholder="Texto do botao"
                  class="mb-2 w-full h-9 px-3 text-sm border rounded-md border-n-weak bg-n-background text-n-slate-12"
                />
                <input
                  v-if="buttonItem.type === 'URL'"
                  v-model="buttonItem.url"
                  placeholder="https://site.com/{{1}}"
                  class="mb-2 w-full h-9 px-3 text-sm border rounded-md border-n-weak bg-n-background text-n-slate-12"
                />
                <input
                  v-if="buttonItem.type === 'URL' && buttonItem.url.includes('{{1}}')"
                  v-model="buttonItem.example"
                  placeholder="Exemplo da variavel da URL"
                  class="mb-2 w-full h-9 px-3 text-sm border rounded-md border-n-weak bg-n-background text-n-slate-12"
                />
                <input
                  v-if="buttonItem.type === 'PHONE_NUMBER'"
                  v-model="buttonItem.phone_number"
                  placeholder="+5511999999999"
                  class="w-full h-9 px-3 text-sm border rounded-md border-n-weak bg-n-background text-n-slate-12"
                />
                <input
                  v-if="buttonItem.type === 'COPY_CODE'"
                  v-model="buttonItem.example"
                  placeholder="Codigo de exemplo"
                  class="w-full h-9 px-3 text-sm border rounded-md border-n-weak bg-n-background text-n-slate-12"
                />
                <template v-if="buttonItem.type === 'FLOW'">
                  <input
                    v-model="buttonItem.flow_id"
                    placeholder="ID do Flow"
                    class="mb-2 w-full h-9 px-3 text-sm border rounded-md border-n-weak bg-n-background text-n-slate-12"
                  />
                  <div class="grid grid-cols-2 gap-2">
                    <select
                      v-model="buttonItem.flow_action"
                      class="h-9 px-2 text-xs border rounded-md border-n-weak bg-n-background text-n-slate-12"
                    >
                      <option value="NAVIGATE">Navegar</option>
                      <option value="DATA_EXCHANGE">Enviar dados</option>
                    </select>
                    <input
                      v-model="buttonItem.navigate_screen"
                      placeholder="Tela inicial"
                      class="h-9 px-3 text-sm border rounded-md border-n-weak bg-n-background text-n-slate-12"
                    />
                  </div>
                </template>
                <p
                  v-if="buttonItem.type === 'CATALOG'"
                  class="m-0 text-xs text-n-slate-10"
                >
                  Usa o catalogo vinculado a conta WhatsApp na Meta.
                </p>
              </div>
            </section>

            <section class="rounded-lg border border-n-weak bg-n-background p-3">
              <label class="flex items-center gap-2 text-sm text-n-slate-12">
                <input v-model="form.ttl_enabled" type="checkbox" />
                Definir validade da mensagem
              </label>
              <input
                v-if="form.ttl_enabled"
                v-model="form.message_send_ttl_seconds"
                type="number"
                min="30"
                class="mt-2 w-full h-10 px-3 text-sm border rounded-md border-n-weak bg-n-alpha-2 text-n-slate-12"
              />
            </section>

            <section class="rounded-lg border border-n-weak bg-[#efe7dc] p-3">
              <p class="m-0 mb-2 text-xs font-semibold text-n-slate-10">
                Previa
              </p>
              <div class="rounded-lg bg-white p-3 text-sm text-n-slate-12 shadow-sm">
                <p v-if="headerPreview" class="m-0 mb-2 font-semibold">
                  {{ headerPreview }}
                </p>
                <p class="m-0 whitespace-pre-wrap">{{ previewText }}</p>
                <p v-if="form.footer_text" class="m-0 mt-3 text-xs text-n-slate-10">
                  {{ form.footer_text }}
                </p>
              </div>
              <div v-if="form.buttons.length" class="mt-2 grid gap-1">
                <button
                  v-for="buttonItem in form.buttons"
                  :key="`${buttonItem.type}-${buttonItem.text}`"
                  type="button"
                  class="h-8 rounded-md bg-white text-sm text-n-blue-11"
                >
                  {{ buttonItem.text || 'Botao' }}
                </button>
              </div>
            </section>

            <div class="flex justify-end gap-2 pt-2">
              <Button
                label="Cancelar"
                type="button"
                slate
                :disabled="isSaving"
                @click="selectedTemplate ? selectTemplate(selectedTemplate) : startCreate()"
              />
              <Button
                label="Salvar"
                icon="i-lucide-save"
                type="submit"
                :is-loading="isSaving"
              />
            </div>
          </form>

          <div v-else-if="selectedTemplate" class="p-4 space-y-4">
            <div
              class="rounded-lg border border-n-weak bg-n-background p-4 text-sm text-n-slate-12"
            >
              <p class="m-0 whitespace-pre-wrap">
                {{ bodyText(selectedTemplate) || 'Template sem corpo.' }}
              </p>
              <p
                v-if="footerText(selectedTemplate)"
                class="m-0 mt-4 text-xs text-n-slate-10"
              >
                {{ footerText(selectedTemplate) }}
              </p>
              <div v-if="buttons(selectedTemplate).length" class="mt-4 grid gap-2">
                <button
                  v-for="button in buttons(selectedTemplate)"
                  :key="button.text"
                  type="button"
                  class="h-9 rounded-md border border-n-weak text-sm text-n-blue-11"
                >
                  {{ button.text }}
                </button>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-2 text-sm">
              <div class="rounded-md bg-n-alpha-3 p-3">
                <p class="m-0 text-xs text-n-slate-10">Status</p>
                <p class="m-0 mt-1 font-medium text-n-slate-12">
                  {{ statusLabel(selectedTemplate.status) }}
                </p>
              </div>
              <div class="rounded-md bg-n-alpha-3 p-3">
                <p class="m-0 text-xs text-n-slate-10">Categoria</p>
                <p class="m-0 mt-1 font-medium text-n-slate-12">
                  {{ categoryLabel(selectedTemplate.category) }}
                </p>
              </div>
            </div>

            <div class="flex justify-end gap-2">
              <Button
                label="Excluir"
                icon="i-lucide-trash-2"
                ruby
                faded
                :is-loading="isSaving"
                @click="deleteTemplate"
              />
              <Button label="Editar" icon="i-lucide-pencil" @click="startEdit" />
            </div>
          </div>

          <div v-else class="p-8 text-center text-sm text-n-slate-10">
            Selecione um template ou crie um novo modelo.
          </div>
        </aside>
      </div>
    </section>
  </main>
</template>
